import type { SimBrother, SimMyBrotherhood } from '@cruz/shared';
import { useEffect, useState } from 'react';
import { load, save } from '../lib/storage.ts';
import { simApi } from './api.ts';
import { num } from './format.ts';
import { card, Link } from './ui.tsx';

/*
 * A friendly rivalry: who goes ahead in your brotherhood, and what changed since you last
 * looked («Rocío te ha adelantado»). The standings go by post, then by how far each one is
 * towards the next.
 */

export const nameOf = (b: SimBrother) =>
  b.you ? 'Tú' : (b.nickname ?? `Hermano nº ${num(b.member_number)}`);

/** Higher post first; on the same post, whoever is closer to the next one. */
export function standings(brothers: SimBrother[]): SimBrother[] {
  return [...brothers].sort(
    (a, b) =>
      b.rank_index - a.rank_index ||
      b.progress - a.progress ||
      b.seniority_years - a.seniority_years ||
      a.member_number - b.member_number,
  );
}

type Snapshot = Record<string, { rank: number; ahead: boolean }>;
const snapKey = (careerId: string) => `cruz.sim.rivals.${careerId}`;

/** What changed since the last look on this device: promotions, and who overtook whom. */
export function newsOf(brothers: SimBrother[], before: Snapshot | null): string[] {
  if (!before) return [];
  const order = standings(brothers);
  const me = order.findIndex((b) => b.you);
  const news: string[] = [];
  order.forEach((b, i) => {
    if (b.you) return;
    const was = before[b.member_number];
    if (!was) {
      news.push(`${nameOf(b)} se ha inscrito en la hermandad.`);
      return;
    }
    if (b.rank_index > was.rank)
      news.push(`${nameOf(b)} ha ascendido a ${b.rank_label.toLowerCase()}.`);
    if (b.rank_index < was.rank) news.push(`A ${nameOf(b)} le han quitado el cargo.`);
    const ahead = me >= 0 && i < me;
    if (ahead && !was.ahead) news.push(`${nameOf(b)} te ha adelantado.`);
    if (!ahead && was.ahead) news.push(`Has adelantado a ${nameOf(b)}.`);
  });
  return news;
}

function snapshotOf(brothers: SimBrother[]): Snapshot {
  const order = standings(brothers);
  const me = order.findIndex((b) => b.you);
  return Object.fromEntries(
    order.map((b, i) => [b.member_number, { rank: b.rank_index, ahead: me >= 0 && i < me }]),
  );
}

/** The brotherhood with friends in it, and what is new since last time (once each). */
function useRivals(careerId: string) {
  const [state, setState] = useState<{ mine: SimMyBrotherhood; news: string[] } | null>(null);
  useEffect(() => {
    let alive = true;
    simApi
      .brotherhood()
      .then((mine) => {
        if (!alive || mine.brothers.length < 2) return;
        const news = newsOf(mine.brothers, load<Snapshot | null>(snapKey(careerId), null));
        save(snapKey(careerId), snapshotOf(mine.brothers));
        setState({ mine, news });
      })
      // Friends are a bonus: without them (or offline) the day goes on
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [careerId]);
  return state;
}

const ordinal = (n: number) => `${n}.º`;

/** At the end of the day: where you stand among your friends, and what moved. */
export function BrotherhoodPulse({ careerId }: { careerId: string }) {
  const rivals = useRivals(careerId);
  if (!rivals) return null;
  const order = standings(rivals.mine.brothers);
  const place = order.findIndex((b) => b.you) + 1;
  return (
    <section aria-labelledby="pulse" className={`${card} px-5 py-4`}>
      <p className="text-xs font-semibold tracking-[0.16em] text-gold-700 uppercase dark:text-gold-300">
        Tu hermandad
      </p>
      <h2 id="pulse" className="mt-1 font-serif text-2xl leading-tight font-semibold">
        {place === 1
          ? `Vas ${ordinal(1)} de ${order.length}: el que más lejos ha llegado`
          : `Vas ${ordinal(place)} de ${order.length}`}
      </h2>
      {rivals.news.length > 0 && (
        <ul aria-label="Novedades" className="mt-3 space-y-1.5">
          {rivals.news.slice(0, 4).map((n) => (
            <li
              key={n}
              className={`animate-rise rounded-lg px-3 py-1.5 text-[0.98rem] font-medium ${
                /adelantado a|ascendido|inscrito/.test(n) && !/te ha adelantado/.test(n)
                  ? 'bg-gold/15 text-gold-700 dark:bg-gold/10 dark:text-gold-300'
                  : 'bg-burgundy-300/25 text-burgundy dark:bg-burgundy/25 dark:text-burgundy-300'
              }`}
            >
              {n}
            </li>
          ))}
        </ul>
      )}
      <ol aria-label="Clasificación" className="mt-3 space-y-2">
        {order.slice(0, 5).map((b, i) => (
          <li key={b.member_number} className="flex items-center gap-3">
            <span className="w-7 flex-none text-right font-serif text-lg font-semibold tabular text-gold-700 dark:text-gold-300">
              {ordinal(i + 1)}
            </span>
            <span className="min-w-0 flex-1">
              <span className={`block truncate ${b.you ? 'font-bold' : 'font-medium'}`}>
                {nameOf(b)}
                <span className="font-normal text-ink-soft dark:text-cream/70">
                  {' · '}
                  {b.finished ? 'carrera terminada' : b.rank_label}
                </span>
              </span>
              <span
                aria-hidden
                className="mt-1 block h-1.5 overflow-hidden rounded-full bg-purple/10 dark:bg-cream/10"
              >
                <span
                  className={`block h-full rounded-full ${b.you ? 'bg-purple dark:bg-gold-300' : 'bg-gold'}`}
                  style={{ width: `${Math.max(3, b.progress)}%` }}
                />
              </span>
            </span>
          </li>
        ))}
      </ol>
      <p className="mt-3 text-sm">
        <Link to="/simulador/hermandad">Ver a todos e invitar a más</Link>
      </p>
    </section>
  );
}
